<script setup lang="ts">
import { useLocalizedDateTime } from "~/composables/useLocalizedDateTime";

type Props = {
  endDate: Date;
  startDate: Date;
  finished: boolean;
  started: boolean;
  currentUserWon?: boolean;
  precision?: "seconds" | "relative";
};

const { endDate, startDate, precision } = defineProps<Props>();

type Emits = {
  (e: "product-running-out"): void;
  (e: "product-finished"): void;
};

const emit = defineEmits<Emits>();

const { timeLeft, colorCodedClass } = useCountdown(
  () => endDate,
  {
    countdownRunningOut: () => emit("product-running-out"),
    countdownFinished: () => emit("product-finished"),
  },
  () => precision,
);

const { localizedDateTime: localizedEndDate } = useLocalizedDateTime(
  () => endDate,
  "dateTime",
);
const { localizedDateTime: localizedStartDate } = useLocalizedDateTime(
  () => startDate,
  "dateTime",
);

const narrowFormatTimeLeft = useNarrowCountdownFormat(timeLeft);
</script>

<template>
  <ClientOnly>
    <template #fallback>
      <BaseSkeletonLoader
        :width="100"
        :height="20"
      />
    </template>
    <div data-cy="bid-product-countdown">
      <div class="text-platform-text-2 text-sm">
        <div v-if="!started">
          <span class="mr-0.5">
            {{ $t("product.startsOn") }}
          </span>
          <span data-cy="product-start-date">
            {{ localizedStartDate }}
          </span>
        </div>
        <div
          v-else-if="finished"
          class="font-normal space-x-1"
        >
          <span>
            {{ `${$t("product.finished")}:` }}
          </span>
          <span data-cy="product-finished-date">
            {{ localizedEndDate }}
          </span>
        </div>
        <div v-else-if="!finished">
          <span
            v-if="timeLeft"
            :class="colorCodedClass"
            data-cy="time-left"
          >
            {{ narrowFormatTimeLeft }}
          </span>
          <span v-else-if="timeLeft === undefined">...</span>
          <span v-else>
            {{ $t("product.closing") }}
          </span>
        </div>
      </div>
      <h2
        v-if="finished && currentUserWon"
        class="font-semibold text-platform-success text-2xl uppercase pt-8"
        data-cy="bid-product-countdown-winner"
      >
        {{ $t("product.userIsWinner") }}
      </h2>
    </div>
  </ClientOnly>
</template>
